import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { canCancelExtraTripDay } from '@/utils/extraTripsApprovals';
import SimpleMenu from '../atoms/Menu';
import { ReasonDialog } from './ReasonDialog';

function getCancelExtraTripDayErrorMessage(error: unknown): string {
	if (!isAxiosError(error)) {
		return 'Erro ao cancelar dia da ida extra. Tente novamente mais tarde.';
	}

	const status = error.response?.status;
	const apiMessage = error.response?.data?.message;

	if (status && status < 500 && typeof apiMessage === 'string' && apiMessage) {
		return apiMessage;
	}

	if (status === 403) {
		return 'Você não tem permissão para cancelar este dia da ida extra';
	}

	return 'Erro ao cancelar dia da ida extra. Tente novamente mais tarde.';
}

interface ExtraTripDayMenuProps {
	onCancel?: () => Promise<void>;
	approvalStatus: string;
	dayDate: string;
	dayStatus: string;
}

const ExtraTripDayMenu = ({
	onCancel,
	approvalStatus,
	dayDate,
	dayStatus,
}: ExtraTripDayMenuProps) => {
	const queryClient = useQueryClient();
	const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
	const { mutate: cancelExtraTripDay } = useMutation({
		mutationFn: onCancel ?? (() => Promise.resolve()),
		onSuccess: () => {
			toast.success('Dia da ida extra cancelado com sucesso', {
				theme: 'colored',
				toastId: 'success',
			});
			queryClient.invalidateQueries({
				queryKey: ['approval-pending'],
			});
			queryClient.invalidateQueries({
				queryKey: ['extra-trips-scheduled'],
			});
			setIsCancelDialogOpen(false);
		},
		onError: (error) => {
			toast.error(getCancelExtraTripDayErrorMessage(error), {
				theme: 'colored',
				toastId: 'error',
			});
		},
	});

	const shouldDisableCancelling =
		!onCancel || !canCancelExtraTripDay({ approvalStatus, dayDate, dayStatus });

	return (
		<>
			<SimpleMenu
				options={[
					{
						name: 'Cancelar',
						onClick: () => setIsCancelDialogOpen(true),
						disabled: shouldDisableCancelling,
					},
				]}
				upDotsButton
			/>
			<ReasonDialog
				title="Confirmação de cancelamento"
				description="Tem certeza que deseja cancelar este dia da ida extra?"
				isOpen={isCancelDialogOpen}
				setIsOpen={setIsCancelDialogOpen}
				showReasonField={false}
				onConfirmation={cancelExtraTripDay}
			/>
		</>
	);
};

export default ExtraTripDayMenu;
